import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const enum fields {
    sections = "sections",
    title = "title",
    subtitle = "subtitle",
}

interface SectionModel {
    fields: {
        sectionName: string;
        sectionLink: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.sections]: SectionModel[];
        [fields.title]: string;
        [fields.subtitle]: string;
    };
}

type Props = OwnProps;

export interface InfoCardStyles {
    Title?: InjectableCss;
    SubTitle?: InjectableCss;
    infoCardContainer?: GridContainerProps;
    infoCardLink?: LinkPresentationProps;
    infoCardDiv?: GridItemProps;
    description?: InjectableCss;
    OrStyle?: InjectableCss;
    HomeStyle?: InjectableCss;
    main?: InjectableCss;
    outer?: InjectableCss;
}

export const infoCardStyles: InfoCardStyles = {
    infoCardContainer: {
        css: css`
            margin: 0 auto;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            width: calc(100% + -145px);

            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [css``, css``, css``])}
        `,
    },
    infoCardDiv: {
        width: [12, 12, 12, 4, 4],
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            font-size: 0.875rem;
            padding: 12px;
            border-radius: 3px;
            background-color: ${getColor("primary")};
            color: ${getColor("primary.contrast")};
            text-decoration: none;
            font-family:"Roboto-Bold"
            width: 47%;
            margin: 5px 7px !important;
            ${TypographyStyle} {
                color: ${getColor("primary.contrast")} !important;
            }
            &:hover {
                background-color: #6d6e71;
                color: ${getColor("primary.contrast")};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 9px 20px;
                    `,
                    css`
                        padding: 9px 20px;
                    `,
                    css`
                        padding: 9px 20px;
                        flex-basis: auto !important;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    infoCardLink: {
        css: css`
            width: 100%;
            span {
                width: 100%;
            }
        `,
    },

    description: {
        css: css`
            font-style: normal;
            font-weight: 100;
            font-size: 26px;
            line-height: 28px;
            margin-top: 16px;
            text-align: center;
            color: ${getColor("text.accent")};
            font-family: "Roboto-Medium";
            text-shadow: 0 0 ${getColor("text.accent")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 8px;
                        width: max-content;
                    `,
                    css`
                        margin-top: 8px;
                        width: max-content;
                    `,
                    css`
                        margin-top: 8px;
                        width: max-content;
                    `,
                    null,
                    null,
                ])}
        `,
    },

    Title: {
        css: css`
            font-size: 32px;
            text-align: center;
            padding-bottom: 40px;
            margin-top: 0;
            font-weight: 400;
            color: ${getColor("text.accent")};
            margin-bottom: 24px;
            font-family: "Roboto-Regular" !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 14px;
                        padding-bottom: 14px;
                        font-size: 32px;
                        text-align: center;
                        margin-top: 0;
                        font-weight: 400;
                    `,
                    css`
                        margin-bottom: 14px;
                        padding-bottom: 14px;
                        font-size: 32px;
                        text-align: center;
                        margin-top: 0;
                        font-weight: 400;
                    `,
                    css`
                        margin-bottom: 14px;
                        padding-bottom: 14px;
                        font-size: 38px;
                        text-align: center;
                        margin-top: 0;
                        font-weight: 400;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    SubTitle: {
        css: css`
            font-size: 16px;
            width: 84%;
            margin: -58px auto;
            text-align: center;
            padding-bottom: 101px;
            font-weight: 100;
            color: ${getColor("text.accent")};
            margin-bottom: -82px;
            p {
                font-family: "Roboto-Regular" !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 13px;
                        margin: -8px auto;
                        text-align: center;
                        padding-bottom: 77px;
                        margin-bottom: -65px;
                    `,
                    css`
                        font-size: 13px;
                        margin: -8px auto;
                        text-align: center;
                        padding-bottom: 77px;
                        margin-bottom: -65px;
                    `,
                    css`
                        font-size: 13px;
                        margin: -8px auto;
                        text-align: center;
                        padding-bottom: 77px;
                        margin-bottom: -31px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    OrStyle: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            margin: 33px 0;
            font-size: 16px;
            font-family: "Roboto-Regular" !important;
            color: ${getColor("text.accent")};
        `,
    },
    HomeStyle: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            margin: 0 auto;
            align-items: center;
            font-size: 0.875rem;
            width: 30%;
            padding: 13px;
            border-radius: 3px;
            background-color: ${getColor("primary")};
            color: ${getColor("primary.contrast")};
            -webkit-text-decoration: none;
            text-decoration: none;
            font-family: "Roboto-Bold";
            ${TypographyStyle} {
                color: ${getColor("primary.contrast")} !important;
                text-align: center;
            }
            &:hover {
                background-color: #6d6e71;
                color: ${getColor("primary.contrast")};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 58% !important;
                    `,
                    css`
                        width: 58% !important;
                    `,
                    css``,
                    null,
                    null,
                ])}
        `,
    },
    outer: {
        css: css`
            background-image: url(/UserFiles/Images/StaticPage/Standard-Exterior-Dark.jpg) !important;
            background-repeat: no-repeat !important;
            background-size: cover !important;
            padding: 28px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 28px 10px !important;
                    `,
                    css`
                        padding: 28px 10px !important;
                    `,
                    css``,
                    null,
                    null,
                ])}
        `,
    },
    main: {
        css: css`
            padding-top: 38px;
            padding-bottom: 60px;
            max-width: 764px;
            margin: 0 auto !important;
            background-image: none;
            background-color: #efefef;
            opacity: 0.8;
            ${GridOffset} {
                display: flex;
                justify-content: center;
                align-items: baseline;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 51px;
                        right: 14px;
                    `,
                    css`
                        margin-top: 51px;
                        right: 14px;
                    `,
                    css`
                        margin-top: 51px;
                        right: 14px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = infoCardStyles;

const PageNotFound: React.FC<Props> = ({ fields }) => {
    return (
        <StyledWrapper {...styles.outer}>
            <StyledWrapper {...styles.main}>
                <StyledWrapper {...styles.Title}>{fields.title}</StyledWrapper>
                <StyledWrapper {...styles.SubTitle}>{parse(fields.subtitle, parserOptions)}</StyledWrapper>
                <GridContainer {...styles.infoCardContainer}>
                    {fields.sections.map((section, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <GridItem key={index} {...styles.infoCardDiv}>
                            <Link href={section.fields.sectionLink}>{section.fields.sectionName}</Link>
                        </GridItem>
                    ))}
                </GridContainer>
                <Typography {...styles.OrStyle}>OR</Typography>
                <Typography>
                    {" "}
                    <Link href="/" {...styles.HomeStyle}>
                        {translate("← Back to homepage")}
                    </Link>
                </Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: PageNotFound,
    definition: {
        group: "Basic",
        displayName: "SE Page Not Found Widget",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Tittle",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "text tip",
                placeholder: "text placeholder",
                sortOrder: 0,
            },
            {
                name: fields.subtitle,
                displayName: "SubTittle",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "text tip",
                placeholder: "text placeholder",
            },

            {
                name: fields.sections,
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "General",
                getDisplay: (item: HasFields) => {
                    return item.fields.description;
                },
                fieldDefinitions: [
                    {
                        name: "sectionName",
                        displayName: "Section Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "sectionLink",
                        displayName: "Section Redirect URL",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
